import
  React
from 'react';

import
  styled
from 'styled-components/macro';

import {
  Environment,
  FontFamilies,
  FontSizes,
  Logger,
  Theme,
  formatPhoneNumberDisplay
} from 'common';

import {
  Button,
  H2,
  Text,
} from 'common/components';

import {
  EventMobileTicketSummary
} from 'ui/event/components';

import {
  withCommon,
} from 'common/hocs';

import {
  OrderProvider
} from 'providers';

import {
  MaxWidthContainer
} from 'ui/event/styled';

const Container = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  min-height: 80vh;
  padding: 50px 0;
`;

const SummaryContainer = styled.div`
  background-color: ${Theme.Card.Background};
  border: ${Theme.Card.Border};
  box-shadow: ${Theme.Card.BoxShadow};
  border-radius: ${Theme.Card.BorderRadius};
  overflow: hidden;
`;

const InfoBox = styled.div`
  background-color: ${props => props.isOesenflamfees ? '' : props.isEmail ? '#E3F2FD' : '#E8F5E9'};
  border: 2px solid ${props => props.isEmail ? '#2196F3' : '#4CAF50'};
  box-shadow: ${Theme.Card.BoxShadow};
  border-radius: ${Theme.Card.BorderRadius};
  padding: 15px 20px;
  margin: 0 0 20px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
`;

const InfoBoxTitle = styled(Text)`
  font-family: ${FontFamilies.Bold};
  font-size: ${FontSizes.Smaller};
  text-align: center;
  text-transform: uppercase;
`;

const InfoBoxText = styled(Text)`
  font-size: ${FontSizes.Smaller};
  line-height: 1.5;
  text-align: center;
  word-break: break-word;
`;

const EventPaymentResultUI = props => {

  const [state, setState] = React.useState({
    icon: undefined,
    title: '',
    textLine1: '',
    textLine2: '',
    textLine3: '',
    paymentRef: '',
    buttonText: '',
    buttonUrl: '',
    downloadToken: '',
    contact: ''
  });
  const [selectedTickets, setSelectedTickets] = React.useState(null);

  const isErrorState = props.common.pathParams.result === 'error' || props.common.pathParams.result === 'cancelled';

  const onDownloadClick = async () => {

    props.common.showLoading()

    const searchParams = new URLSearchParams(props.common.location.search);
    const orderRef = searchParams.get('order_id');

    try {
      const response = await fetch(`${Environment.apiHost}payment/download-order`, {
          method: 'POST',
          headers: {
              'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token: state.downloadToken
          }),
      });

      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }

      // Create a blob from the response
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      // Trigger the download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `WT-${orderRef}.pdf`); // Name of the downloaded file
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      props.common.showToast('Oh no, something went wrong, please try again.');
      Logger.error('EventPaymentResult', `onDownloadClick (${orderRef})`, error)
    }
    finally {
      props.common.hideLoading()
    }
  };

  const setSuccessState = (isMerchOnly, isVideoOnly) => {

    setState(prev => ({
      ...prev,
      textLine1: isMerchOnly ? 'Your order has been placed successfully. We will process it soon.' : 'We are preparing your tickets, they will be in your inbox soon.',
      textLine2: isMerchOnly ? '' : 'Note, this process might take up to 10 minutes.',
      downloadToken: isVideoOnly ? undefined : prev.downloadToken
    }));
  }

  const load = async () => {

    if (!props.common?.pathParams?.result) {

      props.common.showToast('Oh no, something went wrong, please try again.');
      return;
    }

    const searchParams = new URLSearchParams(props.common.location.search);
    const orderRef = searchParams.get('order_id');

    if (props.common.pathParams.result === 'success') {

      setState({
        icon: (
          <div style={{ width: '110px', height: '110px', padding: '15px 10px 5px 10px', marginBottom: '5px' }}>
            <svg style={{ width: '100%', height: '100%' }} xmlns="http://www.w3.org/2000/svg" width="33.624" height="33.041" viewBox="0 0 33.624 33.041">
              <g id="Icon_feather-check-circle" data-name="Icon feather-check-circle" transform="translate(-1.497 -1.473)">
                <path id="Path_1017" data-name="Path 1017" d="M33,16.62V18A15,15,0,1,1,24.105,4.29" fill="none" stroke={Theme.Svg.ColorDefault} strokeLinecap="round" strokeLinejoin="round" strokeWidth="3"/>
                <path id="Path_1018" data-name="Path 1018" d="M33,6,18,21.015l-4.5-4.5" fill="none" stroke={Theme.Svg.ColorDefault} strokeLinecap="round" strokeLinejoin="round" strokeWidth="3"/>
              </g>
            </svg>
          </div>
        ),
        title: 'Payment Successful!',
        textLine1: 'Your order has been placed successfully. We will process it soon.',
        textLine2: '',
        textLine3: '',
        paymentRef: orderRef,
        buttonText: 'Home',
        buttonUrl: '/',
        downloadToken: searchParams.get('token'),
        contact: searchParams.get('contact')
      });
    }

    if (props.common.pathParams.result === 'error' || props.common.pathParams.result === 'cancelled') {
      
      setState({
        icon: (
          <svg style={{ width: '150px', height: '150px' }} xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36" fill="none">
            <g clipPath="url(#clip0_1567_876)">
            <path d="M18 6C15.6266 6 13.3066 6.70379 11.3332 8.02236C9.35977 9.34094 7.8217 11.2151 6.91345 13.4078C6.0052 15.6005 5.76756 18.0133 6.23058 20.3411C6.6936 22.6689 7.83649 24.8071 9.51472 26.4853C11.193 28.1635 13.3312 29.3064 15.6589 29.7694C17.9867 30.2324 20.3995 29.9948 22.5922 29.0866C24.7849 28.1783 26.6591 26.6402 27.9776 24.6668C29.2962 22.6935 30 20.3734 30 18C30 14.8174 28.7357 11.7652 26.4853 9.51472C24.2349 7.26428 21.1826 6 18 6ZM18 28C16.0222 28 14.0888 27.4135 12.4443 26.3147C10.7998 25.2159 9.51809 23.6541 8.76121 21.8268C8.00433 19.9996 7.8063 17.9889 8.19215 16.0491C8.578 14.1093 9.53041 12.3275 10.9289 10.9289C12.3275 9.53041 14.1093 8.578 16.0491 8.19215C17.9889 7.8063 19.9996 8.00433 21.8268 8.7612C23.6541 9.51808 25.2159 10.7998 26.3147 12.4443C27.4135 14.0888 28 16.0222 28 18C28 19.3132 27.7413 20.6136 27.2388 21.8268C26.7363 23.0401 25.9997 24.1425 25.0711 25.0711C24.1425 25.9997 23.0401 26.7362 21.8268 27.2388C20.6136 27.7413 19.3132 28 18 28Z" fill={Theme.Svg.ColorDefault}/>
            <path d="M17.9992 20.0707C17.6544 20.0707 17.3238 19.9337 17.08 19.6899C16.8362 19.4461 16.6992 19.1155 16.6992 18.7707V12.7707C16.6992 12.4259 16.8362 12.0953 17.08 11.8515C17.3238 11.6077 17.6544 11.4707 17.9992 11.4707C18.344 11.4707 18.6747 11.6077 18.9185 11.8515C19.1623 12.0953 19.2992 12.4259 19.2992 12.7707V18.7707C19.2992 19.1155 19.1623 19.4461 18.9185 19.6899C18.6747 19.9337 18.344 20.0707 17.9992 20.0707Z" fill={Theme.Svg.ColorDefault}/>
            <path d="M17.9492 24.5195C18.7776 24.5195 19.4492 23.848 19.4492 23.0195C19.4492 22.1911 18.7776 21.5195 17.9492 21.5195C17.1208 21.5195 16.4492 22.1911 16.4492 23.0195C16.4492 23.848 17.1208 24.5195 17.9492 24.5195Z" fill={Theme.Svg.ColorDefault}/>
            </g>
            <defs>
            <clipPath id="clip0_1567_876">
              <rect width="36" height="36" fill={Theme.Svg.ColorDefault}/>
            </clipPath>
            </defs>
          </svg>
        ),
        title: 'Payment Error',
        textLine1: 'Oops! Something went wrong while processing your payment.',
        textLine2: 'We apologize for the inconvenience.',
        paymentRef: orderRef,
        buttonText: 'Retry payment',
        buttonUrl: `/event/payment?retry=true&orderId=${orderRef?.replace('WT-', '')}`
      });
    }

    if (props.common.pathParams.result === 'expired') {

      setState({
        icon: (
          <svg style={{ width: '110px', height: '110px' }} xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36" fill="none">
            <circle cx="18" cy="18" r="15" stroke={Theme.Svg.ColorDefault} strokeWidth="2" fill="none"/>
            <path d="M12 12L24 24M24 12L12 24" stroke={Theme.Svg.ColorDefault} strokeWidth="2" strokeLinecap="round"/>
          </svg>
        ),
        title: 'Payment Expired',
        textLine1: 'Oops! Your payment session has expired.',
        textLine2: '',
        paymentRef: orderRef,
        buttonText: 'Home',
        buttonUrl: '/',
      });
    }

    OrderProvider.getOrderByOrderId(orderRef?.replace('WT-', ''), props.common.pathParams.result)
      .then(ret => {
        if (ret.ok &&
          (Object.keys(
            ret.data?.selectedTickets || {}).length > 0 ||
            Object.keys(ret.data?.selectedMerch || {}).length  > 0 ||
            Object.keys(ret.data?.selectedPackages || {}).length  > 0)
        ) {
          setSelectedTickets(ret.data);

          if(props.common.pathParams.result === 'success') {

            const isMerchOnly = Object.keys(ret.data?.selectedMerch || {}).length > 0 && Object.keys(ret.data?.selectedTickets || {}).length === 0;
            const isVideoOnly = Object.keys(ret.data?.selectedMerch || {}).length === 1 && Object.keys(ret.data?.selectedMerch || {})[0] === '34';

            setSuccessState(isMerchOnly, isMerchOnly && isVideoOnly);
          }
        }
        else {
          Logger.error('EventPaymentResult', 'OrderProvider.getOrderByOrderId', `Unable to load order after payment for order ${orderRef}`);
        }
      })
      .catch(e => Logger.error('EventPaymentResult', `OrderProvider.getOrderByOrderId (${orderRef})`, e))
      .finally(() => props.common.hideLoading());
  }

  React.useEffect(() => {
    
    setTimeout(() => {
      load()
        .catch(e => Logger.error('EventPaymentResult', 'load', e)); // TODO: show error and log it
    }, 2000);
  }, []); // eslint-disable-line

  return (

    <Container>
      {state.icon}
      <H2>{state.title}</H2>
      <Text padding={'0 15px'} textAlign={'center'}>{state.textLine1}</Text>
      { state.textLine2 && <Text padding={'0 15px'} textAlign={'center'}>{state.textLine2}</Text> }
      { state.textLine3 && <Text padding={'0 15px'} textAlign={'center'}>{state.textLine3}</Text> }
      <Text padding={'15px 15px 15px'} textAlign={'center'}><b>Order ref: {state.paymentRef}</b></Text>

      <MaxWidthContainer>
        {props.common.pathParams.result === 'success' && state.contact && (
          <InfoBox isEmail={state.contact?.includes('@')} isOesenflamfees={window.location.href.includes('oesenvlamfees')}>
            <IconContainer>
              {state.contact?.includes('@') ? (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z" fill="#2196F3"/>
                </svg>
              ) : (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" fill="#4CAF50"/>
                </svg>
              )}
            </IconContainer>
            <InfoBoxTitle>
              {state.contact?.includes('@') ? 'Check your EMAIL' : 'Check your WHATSAPP'}
            </InfoBoxTitle>
            <InfoBoxText>
              <span style={{ fontFamily: FontFamilies.Bold }}>
                {state.contact?.includes('@') ? state.contact : formatPhoneNumberDisplay(state.contact)}
              </span>
            </InfoBoxText>
          </InfoBox>
        )}
        { !window.location.href.includes('etiquetteacademysa')
            && !isErrorState
            && state.downloadToken
            && selectedTickets?.status === 'paid'
            &&
          <>
          <SummaryContainer>
            <Text
              fontSize={FontSizes.Small}
              padding={'15px'}
              textAlign={'center'}>
                You also have the option to download your tickets directly by clicking the button below.
            </Text>
            <div style={{ padding: '0 20px 20px 20px' }}>
              <Button
                text="Download Tickets"
                onClick={onDownloadClick}
                width="100%"
              />
            </div>
          </SummaryContainer>
          <div style={{ height: '20px' }} />
          </>
        }

        { !!selectedTickets &&

          <SummaryContainer>
            <EventMobileTicketSummary
                totals={selectedTickets}
                hideCheckoutButton={true}>

                <Button
                  text={state.buttonText ?? 'Home'}
                  width={'100%'}
                  onClick={() => props.common.navigate(state.buttonUrl ?? '/')}
                />
              </EventMobileTicketSummary>
            </SummaryContainer>
          }

          { !selectedTickets &&
          
            <Button
              text={state.buttonText ?? 'Home'}
              width={'100%'}
              onClick={() => props.common.navigate(state.buttonUrl ?? '/')}
            />
          }

          { isErrorState &&

            <Text
              textAlign={'center'}
              width={'100%'}
              fontSize={FontSizes.Small}
              padding={'10px 0'}
              textDecoration={'underline'}
              onClick={() => props.common.navigate('/')}>

              Go Home
            </Text>
          }
        </MaxWidthContainer>
    </Container>
  );
};

export const EventPaymentResult = withCommon(EventPaymentResultUI);